import React, { useState, useCallback } from 'react';
import { any } from 'ramda';
import EDExpansionPanel from '../../BaseComponents/Accordion/EDExpansionPanel';
import EDTypography from '../../BaseComponents/Typography/EDTypography';
import EDSwitch from '../../BaseComponents/Switch/EDSwitch';
import EDGrid from '../../BaseComponents/GridComponents/EDGrid';
import { ITask, FakeTasksQuery } from './taskQueries';
import { getTodaysDate, getTodaysDateUnformatted } from '../../Utils/utils';
import {
  groupByTasks,
  groupByDatabase,
  getTaskText,
  getLabel,
} from './Tasks.utils';
import { ApolloError, useQuery, gql } from '@apollo/client';

export interface ISubgroup {
  name: string;
  tasks: ITask[];
}

export interface IGroup {
  name: string;
  numberOfTasks: number;
  subgroup: ISubgroup[];
}

interface ITasksData {
  me: Array<{ databases: Array<{ id: string; name: string }> | null }> | null;
}

const tasksQuery = gql`
  query tasksComponentQuery {
    me {
      databases {
        id
        name
      }
    }
  }
`;

const shouldShowDatabases = (
  loading: boolean,
  error?: ApolloError,
  data?: ITasksData
): boolean => {
  if (!loading && !error) {
    const me = data && data.me ? data.me[0] : undefined;
    if (me && me.databases && me.databases.length > 1) {
      return true;
    }
    return false;
  }
  return false;
};

/**
 * En lista över uppgifter som hämtas från backend.
 */
const Tasks: React.FC<React.PropsWithChildren<{}>> = props => {
  const [sortByDatabase, setSortByDatabase] = useState(false);
  const { loading, error, data } = useQuery<ITasksData>(tasksQuery);
  const showDatabases = shouldShowDatabases(loading, error, data);

  const onSortChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setSortByDatabase(checked);
    },
    []
  );

  return (
    // TODO: Change back to real tasks query after presentation
    <FakeTasksQuery>
      {(loading: boolean, tasks: ITask[]) => {
        const groupedTasks = sortByDatabase
          ? groupByDatabase(tasks)
          : groupByTasks(tasks);

        return (
          <React.Fragment>
            {loading && <EDTypography>laddar...</EDTypography>}

            {showDatabases && (
              <EDGrid
                container
                alignItems="center"
                justifyContent="space-between"
                spacing={0}
              >
                <EDGrid item>
                  <EDTypography variant="body2" style={{ paddingLeft: '16px' }}>
                    Sortera på enhet
                  </EDTypography>
                </EDGrid>
                <EDGrid item>
                  <EDSwitch checked={sortByDatabase} onChange={onSortChanged} />
                </EDGrid>
              </EDGrid>
            )}

            {groupedTasks.map((x, index) => {
              const todaysDate = getTodaysDateUnformatted();
              const isExpired = any(
                subGroup =>
                  any(task => {
                    const deadline = task.DEADLINE;
                    if (deadline == null) {
                      return false;
                    } else {
                      return Date.parse(deadline) < Date.parse(todaysDate);
                    }
                  }, subGroup.tasks),
                x.subgroup
              );

              return (
                <EDExpansionPanel
                  key={index}
                  label={getLabel(x.name, x.numberOfTasks, isExpired)}
                  elevation={0}
                >
                  <ul
                    style={{
                      listStyleType: 'none',
                      padding: 0,
                      margin: 0,
                    }}
                  >
                    {x.subgroup.map((d, di) => {
                      const todaysDate = getTodaysDate();
                      const isExpired = any(task => {
                        const deadline = task.DEADLINE;
                        if (deadline == null) {
                          return false;
                        } else {
                          return Date.parse(deadline) < Date.parse(todaysDate);
                        }
                      }, d.tasks);
                      return (
                        <li
                          key={di}
                          style={{ marginBottom: 10, marginLeft: '40px' }}
                        >
                          {(showDatabases || sortByDatabase) && (
                            <EDTypography
                              variant="body2"
                              color={isExpired ? 'error' : 'initial'}
                            >
                              {d.name}
                            </EDTypography>
                          )}
                          <EDTypography
                            variant="body2"
                            component="div"
                            color={isExpired ? 'error' : 'initial'}
                          >
                            {d.tasks.map((t, i) =>
                              getTaskText(
                                `${
                                  t.ANTAL && t.ANTAL > 1
                                    ? '(' + t.ANTAL + ' st) - '
                                    : ''
                                }${
                                  t.DEADLINE ? t.DEADLINE : 'Inget slutdatum'
                                }`,
                                t.ProcAktivitetID,
                                t.DBNAME,
                                i
                              )
                            )}
                          </EDTypography>
                        </li>
                      );
                    })}
                  </ul>
                </EDExpansionPanel>
              );
            })}
          </React.Fragment>
        );
      }}
    </FakeTasksQuery>
  );
};

export default Tasks;
